/**
 * Predictive search dropdown.
 *
 * Wires every <form data-predictive-search> with a debounced fetch to the
 * server-rendered `predictive-search` section (Section Rendering API), which
 * filters every suggestion through the access-control gate, and injects the
 * grouped results into [data-predictive-results]. Keyboard nav (↑/↓/Enter/Esc),
 * click-outside-to-close, and an "Show all results" footer link included.
 * (The focus/empty state — recently-viewed + popular — is still built client-side.)
 */
(() => {
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 220;
  const LIMIT = 8;

  const escapeHtml = (str) =>
    String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  // /search/suggest.json returns prices as strings in MAJOR currency units
  // (e.g. "22.43" for €22.43) — not cents. Prefer the shared formatter from
  // assets/money.js (uses Intl.NumberFormat); fall back to a Croatian-flavoured
  // local format if the helper isn't loaded.
  const formatMoney = (amount) => {
    if (amount == null || amount === '') return '';
    const num = Number(amount);
    if (!Number.isFinite(num)) return '';
    if (window.BeautyOutletMoney?.format) {
      return window.BeautyOutletMoney.format(num, { unit: 'major' });
    }
    const iso = window.theme?.currency?.iso || 'EUR';
    try {
      return new Intl.NumberFormat(window.theme?.locale || 'hr-HR', {
        style: 'currency',
        currency: iso,
      }).format(num);
    } catch (_) {
      const symbol = window.theme?.currency?.symbol || '€';
      return `${num.toFixed(2).replace('.', ',')} ${symbol}`;
    }
  };

  // Translatable strings — fall back to the Croatian defaults so the dropdown
  // still works if `snippets/theme-globals.liquid` isn't on the page.
  const t = (key, fallback) => {
    const dict = window.theme?.strings?.predictive || {};
    return dict[key] || fallback;
  };
  const interpolate = (str, params) =>
    Object.entries(params || {}).reduce(
      (acc, [k, v]) => acc.replaceAll(`__${k.toUpperCase()}__`, v),
      String(str ?? ''),
    );

  const renderProductPrice = (p) => {
    const min = p.price_min ?? p.price;
    const max = p.price_max ?? p.price;
    if (min == null) return '';
    if (max != null && Number(min) !== Number(max)) {
      return `<span class='predictive__item-price'>${formatMoney(min)} – ${formatMoney(max)}</span>`;
    }
    return `<span class='predictive__item-price'>${formatMoney(min)}</span>`;
  };

  const renderProduct = (p) => {
    const img = p.featured_image?.url || p.image || '';
    const vendor = p.vendor ? `<span class='predictive__item-vendor'>${escapeHtml(p.vendor)}</span>` : '';
    return `
      <a class='predictive__item' role='option' href='${escapeHtml(p.url)}' data-predictive-item>
        <span class='predictive__item-thumb'>
          ${img ? `<img src='${escapeHtml(img)}' alt='' loading='lazy' width='48' height='48'>` : ''}
        </span>
        <span class='predictive__item-body'>
          <span class='predictive__item-title'>${escapeHtml(p.title)}</span>
          ${vendor}
        </span>
        ${renderProductPrice(p)}
      </a>`;
  };

  const renderLink = (item) => `
    <a class='predictive__item predictive__item--simple' role='option' href='${escapeHtml(item.url)}' data-predictive-item>
      <span class='predictive__item-title'>${escapeHtml(item.title)}</span>
    </a>`;

  const renderGroup = (label, items, renderer) => {
    if (!items || items.length === 0) return '';
    return `
      <div class='predictive__group'>
        <h3 class='predictive__group-title'>${escapeHtml(label)}</h3>
        <div class='predictive__group-list'>${items.map(renderer).join('')}</div>
      </div>`;
  };

  const renderSkeleton = () => `
    <div class='predictive__loading' aria-live='polite'>
      <div class='predictive__skel'></div>
      <div class='predictive__skel'></div>
      <div class='predictive__skel'></div>
    </div>`;

  const renderEmpty = (terms) => {
    const tpl = t('noResults', 'Nema rezultata za „__TERMS__"');
    return `<p class='predictive__empty'>${interpolate(tpl, { terms: escapeHtml(terms) })}</p>`;
  };

  // ── Empty-state defaults ─────────────────────────────────────────────
  // When the user focuses the input without typing, we show two helpful
  // groups: their recently-viewed products (if any) and a few popular
  // categories from the main menu. Fetching product data is cached per
  // page load so repeatedly opening/closing the dropdown doesn't refetch.
  const defaultStateCache = { items: null, fetched: null };

  const fetchProductByHandle = async (handle) => {
    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const p = await res.json();
      // Normalise to the same shape `renderProduct` expects.
      return {
        title: p.title,
        url: p.url,
        vendor: p.vendor,
        price: (p.price ?? 0) / 100, // shop endpoint returns cents
        featured_image: { url: p.featured_image },
      };
    } catch {
      return null;
    }
  };

  // Read the recently-viewed handle list. Prefer the `window.RecentlyViewed`
  // API when present; otherwise fall back to localStorage so the empty
  // state still works on pages that don't load `recently-viewed.js`
  // (e.g. the homepage and collection pages — the script only loads with
  // the `recently-viewed` section, but the localStorage key persists).
  const readRecentlyViewedHandles = () => {
    if (typeof window.RecentlyViewed?.read === 'function') {
      try {
        const v = window.RecentlyViewed.read();
        if (Array.isArray(v)) return v;
      } catch {
        /* fall through */
      }
    }
    try {
      const raw = localStorage.getItem('beautyoutlet:recently-viewed');
      const v = JSON.parse(raw || '[]');
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  };

  const loadRecentlyViewed = async (limit = 4) => {
    if (defaultStateCache.items) return defaultStateCache.items;
    const handles = readRecentlyViewedHandles();
    if (handles.length === 0) {
      defaultStateCache.items = [];
      return [];
    }
    // Fetch in parallel with a hard cap so we don't spam the storefront.
    const slice = handles.slice(0, limit);
    const results = await Promise.all(slice.map(fetchProductByHandle));
    const items = results.filter(Boolean);
    defaultStateCache.items = items;
    return items;
  };

  const renderDefault = (panel) => {
    const popular = window.theme?.search?.popular || [];
    const recentLabel = t('recentlyViewed', 'Nedavno pregledano');
    const popularLabel = t('popular', 'Popularne kategorije');

    // Render the popular group immediately; insert the recently-viewed
    // group asynchronously once the product data resolves. This keeps
    // the dropdown opening instantly even on a cold cache.
    const initial = renderGroup(popularLabel, popular, renderLink);
    panel.innerHTML = `<div data-predictive-default-recent></div>${initial}`;

    loadRecentlyViewed().then((items) => {
      const slot = panel.querySelector('[data-predictive-default-recent]');
      if (!slot) return; // panel was re-rendered for live results — drop it
      slot.outerHTML = renderGroup(recentLabel, items, renderProduct);
    });
  };

  const init = (form) => {
    const input = form.querySelector('[data-predictive-input]');
    const panel = form.querySelector('[data-predictive-results]');
    if (!input || !panel) return;

    let debounce = null;
    let abort = null;
    let lastTerms = '';

    const open = () => {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      setActive(-1);
    };

    const items = () => Array.from(panel.querySelectorAll('[data-predictive-item]'));
    let activeIdx = -1;
    const setActive = (idx) => {
      const all = items();
      all.forEach((el, i) => el.classList.toggle('is-active', i === idx));
      activeIdx = idx;
      if (idx >= 0 && all[idx]) {
        all[idx].scrollIntoView({ block: 'nearest' });
      }
    };

    const fetchResults = async (terms) => {
      if (abort) abort.abort();
      abort = new AbortController();
      panel.innerHTML = renderSkeleton();
      open();
      // Server-rendered results via the Section Rendering API. The
      // `predictive-search` section filters every suggestion through the
      // access-control gate (tier-aware: guests see none, VIP see VIP, ORBICO
      // see all), so restricted items never reach the browser — which
      // /search/suggest.json cannot do. See sections/predictive-search.liquid.
      const params = new URLSearchParams({
        q: terms,
        section_id: 'predictive-search',
        'resources[type]': 'product,article,collection',
        'resources[limit]': String(LIMIT),
      });
      try {
        const res = await fetch(`/search/suggest?${params.toString()}`, {
          signal: abort.signal,
        });
        if (!res.ok) throw new Error('predictive-search request failed');
        const html = await res.text();
        if (terms !== lastTerms) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rendered = doc.querySelector('[data-predictive-rendered]');
        panel.innerHTML = rendered ? rendered.innerHTML : renderEmpty(terms);
        open();
      } catch (e) {
        if (e?.name === 'AbortError') return;
        panel.innerHTML = renderEmpty(terms);
        open();
      }
    };

    input.addEventListener('input', () => {
      const terms = input.value.trim();
      lastTerms = terms;
      clearTimeout(debounce);
      if (terms.length < MIN_CHARS) {
        if (abort) abort.abort();
        // Empty / too short: show the default panel (recently-viewed +
        // popular categories) instead of closing entirely. Users get
        // useful suggestions while typing the first character or two.
        renderDefault(panel);
        open();
        return;
      }
      debounce = setTimeout(() => fetchResults(terms), DEBOUNCE_MS);
    });

    input.addEventListener('keydown', (e) => {
      const all = items();
      if (e.key === 'ArrowDown') {
        if (panel.hidden) return;
        e.preventDefault();
        setActive(Math.min(activeIdx + 1, all.length - 1));
      } else if (e.key === 'ArrowUp') {
        if (panel.hidden) return;
        e.preventDefault();
        setActive(Math.max(activeIdx - 1, -1));
      } else if (e.key === 'Enter') {
        if (!panel.hidden && activeIdx >= 0 && all[activeIdx]) {
          e.preventDefault();
          window.location.href = all[activeIdx].getAttribute('href');
        }
        // Otherwise: let form submit naturally to /search?q=…
      } else if (e.key === 'Escape') {
        close();
        input.blur();
      }
    });

    input.addEventListener('focus', () => {
      const terms = input.value.trim();
      if (terms.length >= MIN_CHARS && panel.innerHTML.trim()) {
        // Reopen the cached live results — don't refetch.
        open();
        return;
      }
      // Empty focus: show default state.
      renderDefault(panel);
      open();
    });

    document.addEventListener('click', (e) => {
      if (!form.contains(e.target)) close();
    });
  };

  const ready = () => {
    document.querySelectorAll('[data-predictive-search]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }
})();
